import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { queryClient } from '../App'
import { FC } from '.'
import { getDateObj } from './Functions'

const time = {
  seconds: 1000,
  minutes: 60 * 1000,
  hours: 60 * 60 * 1000
}

const queryConfig = {
  rare: {
    staleTime: 1 * time.hours,
    cacheTime: 2 * time.hours
  },
  frequent: {
    staleTime: 5 * time.minutes,
    cacheTime: 10 * time.minutes
  },
  autoFetchFrequent: {
    staleTime: 5 * time.minutes,
    cacheTime: 10 * time.minutes,
    refetchInterval: 10 * time.seconds
  }
}

const queryNotFound = {
  config: {},
  queryFn: (key) => {
    throw new Error('Query Not Found: ' + key)
  }
}

const DAY_MS = 24 * 60 * 60 * 1000

const queries = {
  environments: {
    config: queryConfig.rare,
    queryFn: () => FC.service('environments').find({})
  },
  environment: {
    config: queryConfig.rare,
    queryFn: async ({ queryKey: [key, id] }) => {
      if (!id || id === 'new') return { id: 'new' }
      const res = await FC.service('environments').get(id)
      return res
    }
  },
  users: {
    config: queryConfig.rare,
    queryFn: () => FC.service('users').find({})
  },
  user: {
    config: queryConfig.rare,
    queryFn: async ({ queryKey: [key, id] }) => {
      if (!id || id === 'new') return { id: 'new' }
      const res = await FC.service('users').get(id)
      return res
    }
  },
  travellers: {
    config: queryConfig.rare,
    queryFn: () => FC.service('travellers').find({})
  },
  traveller: {
    config: queryConfig.rare,
    queryFn: async ({ queryKey: [key, id] }) => {
      if (!id || id === 'new') {
        return { id: 'new' }
      }
      const res = await FC.service('travellers').get(id)
      return res
    }
  },
  prompts: {
    config: queryConfig.rare,
    queryFn: async () => {
      const res = await FC.service('prompts').find({})
      return res
    }
  },
  prompt: {
    config: queryConfig.rare,
    queryFn: async ({ queryKey: [key, id] }) => {
      if (!id || id === 'new') return null
      const res = await FC.service('prompts').get(id)
      return res
    }
  },
  units: {
    config: queryConfig.rare,
    queryFn: () => []
  },
  processes: {
    config: queryConfig.autoFetchFrequent,
    queryFn: ({ queryKey: [key, selectedDate] }) =>
      selectedDate
        ? FC.service('process').find({
            query: {
              $and: [
                {
                  createdAt: {
                    $gte: new Date(
                      selectedDate.getFullYear(),
                      selectedDate.getMonth(),
                      selectedDate.getDate(),
                      0,
                      0,
                      0,
                      0
                    )
                  }
                },
                {
                  createdAt: {
                    $lte: new Date(
                      selectedDate.getFullYear(),
                      selectedDate.getMonth(),
                      selectedDate.getDate(),
                      23,
                      59,
                      59,
                      999
                    )
                  }
                }
              ]
            }
          })
        : []
  },
  getStats: {
    config: queryConfig.autoFetchFrequent,
    queryFn: ({ queryKey: [key, startDate, endDate] }) => {
      if (!startDate || !endDate) return []
      return FC.service('info').get('getStats', {
        query: { startDate: getDateObj(startDate), endDate: getDateObj(endDate) }
      })
    }
  },
  pricingPerProcess: {
    config: queryConfig.autoFetchFrequent,
    queryFn: ({ queryKey: [key, processId] }) => {
      if (!processId) return []
      return FC.service('info').get('pricingPerProcess', { query: { processId } })
    }
  },
  pricingPerConversation: {
    config: queryConfig.autoFetchFrequent,
    queryFn: ({ queryKey: [key, conversationId] }) => {
      if (!conversationId) return []
      return FC.service('info').get('pricingPerConversation', { query: { conversationId } })
    }
  },
  totalPricing: {
    config: queryConfig.rare,
    queryFn: ({ queryKey: [key, startDate, endDate] }) => {
      return FC.service('info').get('totalPricing', {
        query: { startDate: getDateObj(startDate), endDate: getDateObj(endDate) }
      })
    }
  },
  getDashboardStatsConversations: {
    config: queryConfig.rare,
    queryFn: ({ queryKey: [key, startDate, endDate, filters = {}] }) => {
      const { isola, unitOp, codCliente, status } = filters
      return FC.service('info').get('getDashboardStatsConversations', {
        query: {
          startDate,
          endDate,
          ...(isola && { isola }),
          ...(unitOp && { unitOp }),
          ...(codCliente && { codCliente }),
          ...(status && { statusFilter: status })
        }
      })
    }
  },
  processSteps: {
    config: queryConfig.autoFetchFrequent,
    queryFn: ({ queryKey: [key, processId] }) =>
      processId ? FC.service('processStep').find({ query: { processId } }) : []
  },
  errorProcessStepsRange: {
    config: queryConfig.rare,
    queryFn: ({ queryKey: [key, startDate, endDate] }) =>
      startDate && endDate
        ? FC.service('processStep').find({
            query: {
              $or: [{ stepState: 5 }, { stepState: 6 }],
              $and: [
                { createdAt: { $gte: new Date(startDate) } },
                { createdAt: { $lte: new Date(new Date(endDate).getTime() + DAY_MS) } }
              ]
            }
          })
        : []
  },
  unitPerformanceStats: {
    config: queryConfig.frequent,
    queryFn: ({ queryKey: [key, startDate, endDate] }) =>
      startDate && endDate
        ? FC.service('info').get('unitPerformanceStats', {
            query: { startDate: startDate.toISOString(), endDate: endDate.toISOString() }
          })
        : []
  },
  unitPerformanceTimeSeries: {
    config: queryConfig.frequent,
    queryFn: ({ queryKey: [key, startDate, endDate, unitFilter, bucketSize] }) =>
      startDate && endDate
        ? FC.service('info').get('unitPerformanceTimeSeries', {
            query: {
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString(),
              ...(unitFilter && unitFilter !== '__ALL__' && { unitFilter }),
              ...(bucketSize && { bucketSize })
            }
          })
        : []
  },
  unitPerformanceProcessDaily: {
    config: queryConfig.frequent,
    queryFn: ({ queryKey: [key, startDate, endDate] }) =>
      startDate && endDate
        ? FC.service('info').get('unitPerformanceProcessDaily', {
            query: {
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString()
            }
          })
        : []
  },
  unitPerformanceOverview: {
    config: queryConfig.frequent,
    queryFn: ({ queryKey: [key, startDate, endDate] }) =>
      startDate && endDate
        ? FC.service('info').get('unitPerformanceOverview', {
            query: {
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString()
            }
          })
        : {}
  },
  unitPerformanceAnomalies: {
    config: {
      ...queryConfig.frequent,
      refetchInterval: 60 * 1000
    },
    queryFn: () => FC.service('info').get('unitPerformanceAnomalies', { query: {} })
  },
  unitPerformanceLive: {
    config: {
      ...queryConfig.frequent,
      refetchInterval: 30 * 1000
    },
    queryFn: () => FC.service('info').get('unitPerformanceLive', { query: {} })
  },
  unitPerformanceDrillDown: {
    config: queryConfig.frequent,
    queryFn: ({ queryKey: [key, unit, startDate, endDate, minExecutionTime, stateFilter] }) =>
      unit && startDate && endDate
        ? FC.service('info').get('unitPerformanceDrillDown', {
            query: {
              unit,
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString(),
              ...(minExecutionTime && { minExecutionTime }),
              ...(stateFilter && { stateFilter })
            }
          })
        : []
  },
  conversations: {
    config: queryConfig.frequent,
    queryFn: ({ queryKey: [key, selectedDate, isola] }) =>
      selectedDate
        ? FC.service('info').get('getConversationsOperatore', {
            query: { date: selectedDate, isola }
          })
        : []
  },
  isoleConversations: {
    config: queryConfig.frequent,
    queryFn: ({ queryKey: [key] }) => FC.service('info').get('getIsoleConversations')
  },
  agenticoAttivo: {
    config: queryConfig.frequent,
    queryFn: () => FC.service('info').get('getAgenticoAttivo')
  },
  unitOpConversations: {
    config: queryConfig.frequent,
    queryFn: ({ queryKey: [key] }) => FC.service('info').get('getUnitOpConversations')
  },
  codClientiConversations: {
    config: queryConfig.frequent,
    queryFn: ({ queryKey: [key] }) => FC.service('info').get('getCodClientiConversations')
  },
  conversation: {
    config: {
      ...queryConfig.frequent,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: (failureCount, error) => {
        if (error?.toString()?.includes('Conversation not found')) return false
        if (failureCount < 3) return true
      }
    },
    queryFn: ({ queryKey: [key, conversationId] }) =>
      conversationId
        ? FC.service('info').get('getConversationOperatore', { query: { conversationId } })
        : null
  },
  adjacentConversations: {
    config: {
      ...queryConfig.frequent,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    },
    queryFn: ({ queryKey: [key, conversationId] }) =>
      conversationId
        ? FC.service('info').get('getAdjacentConversations', { query: { conversationId } })
        : { prev: false, next: false }
  },
  /**
   * Turni del motore agentico di una conversazione: la controparte di `processes`.
   * L'audit vive nel database dell'Executor, BusinessAI fa da proxy.
   */
  agentTurns: {
    config: queryConfig.autoFetchFrequent,
    queryFn: ({ queryKey: [key, conversationId] }) =>
      conversationId
        ? FC.service('info').get('getAgentTurns', { query: { conversationId } })
        : { turns: [], queue: {} }
  },
  communityPresets: {
    config: queryConfig.rare,
    queryFn: () => FC.service('communityPreset').find()
  },
  communityPreset: {
    config: queryConfig.rare,
    queryFn: ({ queryKey: [key, presetId] }) =>
      presetId && presetId !== 'new'
        ? FC.service('communityPreset').find({ query: { id: presetId } })
        : []
  },
  adminCoefficient: {
    config: queryConfig.rare,
    queryFn: () => FC.service('adminCoefficient').get(1)
  },
  system: {
    config: queryConfig.rare,
    queryFn: () => FC.service('system').find({})
  },
  autoTests: {
    config: queryConfig.rare,
    queryFn: () => FC.service('autoTests').find()
  },
  autoTest: {
    config: {
      ...queryConfig.rare,
      retry: (failureCount, error) => {
        console.log('error', error?.toString())
        if (error?.toString()?.includes('No record found for id')) return false
        if (failureCount < 3) return true
      }
    },
    queryFn: ({ queryKey: [key, autoTestId] }) =>
      autoTestId ? FC.service('autoTests').get(autoTestId) : null
  }
}

export const useCQuery = (queryArgs, extraQueries = []) => {
  const queryKey = Array.isArray(queryArgs) ? queryArgs : [queryArgs]
  const { queryFn, config } = { ...queries, ...extraQueries }?.[queryKey[0]] || queryNotFound
  return useQuery({ queryKey, queryFn, ...config })
}

export const prefetchQuery = (queryKey, extraQueries = []) => {
  const { queryFn, config } =
    { ...queries, ...extraQueries }[Array.isArray(queryKey) ? queryKey[0] : queryKey] ||
    queryNotFound
  queryClient.prefetchQuery({ queryKey, queryFn, config })
}

/* export const prefetchQueries = (extraQueries) =>
  Object.entries({ ...queries, ...extraQueries }).forEach(([queryKey, { config, queryFn, defaultKeys = [], noInitalPrefetch }]) => {
    const key = [queryKey, ...defaultKeys]
    !noInitalPrefetch && queryClient.prefetchQuery(key, queryFn, config)
  }) */

export const invalidateQuery = (queryKeys) =>
  (Array.isArray(queryKeys) ? queryKeys : [queryKeys]).forEach((queryKey) =>
    queryClient.invalidateQueries({ queryKey: Array.isArray(queryKey) ? queryKey : [queryKey] })
  )

/**
 * Invalida tutte le query lista conversazioni (incluse infinite query).
 * Usata dai listener socket per aggiornare la lista alla creazione/patch di conversazioni.
 */
export const invalidateConversationsList = () =>
  queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'conversations' })

// FETCH QUERY

/* export const fetchQuery = (queryArgs, extraQueries = []) => {
  const queryKey = Array.isArray(queryArgs) ? queryArgs : [queryArgs]
  const { queryFn, config } = { ...queries, ...extraQueries }?.[queryKey[0]] || queryNotFound
  return queryClient.fetchQuery({ queryKey, queryFn, ...config })
} */

// INFINITE QUERIES
const infiniteQueries = {
  conversations: {
    firstPageLength: 20,
    pageLength: 50,
    config: {
      ...queryConfig.frequent,
      keepPreviousData: true
    },
    queryFn:
      (firstPageLength, pageLength) =>
      ({ pageParam = 1, queryKey: [, selectedDate, isola] = [] }) =>
        FC.client.service('info').get('getConversationsOperatore', {
          query: {
            $skip: pageParam === 1 ? 0 : (pageParam - 2) * pageLength + firstPageLength,
            $limit: pageParam === 1 ? firstPageLength : pageLength,
            date: selectedDate,
            isola
          }
        }),
    noInitalPrefetch: true
  }
}

export const useCInfiniteQuery = (queryArgs, extraQueries = []) => {
  const queryKey = Array.isArray(queryArgs) ? queryArgs : [queryArgs]
  const {
    queryFn,
    config,
    firstPageLength = 50,
    pageLength = 50
  } = { ...infiniteQueries, ...extraQueries }?.[queryKey[0]] || queryNotFound

  const {
    data = {},
    isSuccess,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    refetch
  } = useInfiniteQuery({
    queryKey,
    queryFn: queryFn(firstPageLength, pageLength),
    // devo fetchare nuova pagina se sono della lunghezza massima stabilita (firstPageLength o pageLength)
    getNextPageParam: (lastPage, pages) =>
      pages?.length === 1
        ? (Array.isArray(lastPage) ? lastPage?.length : lastPage?.data?.length) === firstPageLength
          ? pages?.length + 1
          : null
        : (Array.isArray(lastPage) ? lastPage?.length : lastPage?.data?.length) === pageLength
          ? pages?.length + 1
          : null,
    ...config
  })

  // controllo se la pagina è un array o un oggetto con data e ritorno un array di pagine.
  // perchè se è definita la paginazione sul server ritorna un oggetto con data e se non è definita ritorna un array
  const pages = data?.pages?.map((page) => (Array.isArray(page) ? page : page.data)).flat()

  useEffect(() => {
    !isFetching && !isFetchingNextPage && hasNextPage && fetchNextPage()
  }, [isFetchingNextPage, isFetching])

  return { data: pages, isSuccess, isFetchingNextPage, refetch }
}
